import { flow, Instance, types } from 'mobx-state-tree'
import { ToastPosition } from '../../comps/Toast'
import { addBeneficiary } from '../../network/addBeneficiary'
import { bulkOnboardingFileDownload } from '../../network/bulkBeneficiaryFileDownload'
import { bulkOnboardingFileUpload } from '../../network/bulkBeneficiaryFileUpload'
import { bulkRefundUploadConfirmReq } from '../../network/bulkRefundUploadConfirm'
import { createConfigOfEntity } from '../../network/createConfig'
import { exportBeneficiariesListFile } from '../../network/exportBulkBeneficiaryFile'
import { extendAppSessionAPI } from '../../network/extendAppSession'
import { extendUserSessionAPI } from '../../network/extendUserSession'
import { fetchAllBeneficiariesList } from '../../network/fetchAllBeneficiariesList'
import { bulkBeneficiaryFileHistory } from '../../network/uploadedFileHistory'
import { uploadFileReq } from '../../network/uploadFileReq'
import { getErrorMessage, getSelectedDate, isEmpty } from '../../util/util'
import { exportType, paginationSettingsWithDefaultPage1 } from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { FieldSetting } from './Transactions'

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})
const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.optional(types.string, ''),
})

const Pagination = types.model('Pagination', {
  size: types.optional(types.number, 0),
  totalElements: types.optional(types.number, 0),
  totalPages: types.optional(types.number, 0),
  number: types.optional(types.number, 0),
})

const Beneficiary = types.model('Beneficiary', {
  legalName: types.optional(types.string, ''),
  identificationNumber: types.optional(types.string, ''),
  bankAccountNumber: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  subType: types.optional(types.string, ''),
  mobileNumber: types.optional(types.string, ''),
  emailId: types.optional(types.string, ''),
  ifscCode: types.optional(types.string, ''),
  vpa: types.optional(types.string, ''),
  cardNumber: types.optional(types.string, ''),
})

const BulkBeneficiary = types.model('BulkBeneficiary', {
  filename: types.optional(types.string, ''),
  uploadDate: types.optional(types.string, ''),
  totalCount: types.optional(types.string, ''),
  addedCount: types.optional(types.string, ''),
  declinedCount: types.optional(types.string, ''),
  actionDownload: types.optional(types.string, ''),
  uploader: types.maybeNull(types.string),
  id: types.maybeNull(types.number),
  accepted: types.maybeNull(types.number),
  declined: types.maybeNull(types.number),
  totalBeneficiaries: types.maybeNull(types.number),
  acceptedBeneficiaries: types.maybeNull(types.number),
  failedBeneficiaries: types.maybeNull(types.number),
  status: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  parentIdentificationNumber: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
  createAt: types.maybeNull(types.string),
})

const Lov = types.model('Lov', {
  key: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
})

const File = types.model('File', {
  filename: types.maybeNull(types.string),
  uploader: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  responseFile: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
  accepted: types.maybeNull(types.number),
  declined: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
})

const BeneficiaryOnboarding = types
  .model('Beneficiary', {
    id: types.optional(types.string, ''),
    selectedDate: DateRange,
    bulkSelectedDateInfo: DateRange,
    pagination: Pagination,
    fieldSetting: types.array(FieldSetting),
    beneficiariesList: types.optional(types.array(Beneficiary), []),
    bulkBeneficiaryList: types.optional(types.array(BulkBeneficiary), []),
    uploadStatus: types.optional(
      types.enumeration(['NOT_SELECTED', 'IN_PROGRESS', 'UPLOADED', 'IN_PROCESS', 'PROCESSED']),
      'NOT_SELECTED',
    ),
    size: types.maybeNull(types.number),
    count: types.maybeNull(types.number),
    uploadedFileId: types.optional(types.string, ''),
    lovs: types.optional(types.array(Lov), []),
    uploadedFile: types.maybeNull(File),
    fileUploaded: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    updateBeneficiary: flow(function* updateBeneficiary(_mid: string) {}),
    fetchAllBeneficiaries: flow(function* fetchAllBeneficiaries(
      mid: string,
      type: string,
      pageNumber?: number,
      recordsPerPage?: number,
    ) {
      try {
        const response = yield fetchAllBeneficiariesList(
          mid,
          type,
          RootStore.user.session.getTokens,
          pageNumber ?? self.pagination.number,
          recordsPerPage ?? 10,
        )
        if (response) {
          self.beneficiariesList = response?.response
          if (response?.response?.length > 0) {
            self.pagination = JSON.parse(JSON.stringify(response?.page))
          } else {
            self.pagination = JSON.parse(JSON.stringify({ size: 0, totalElements: 0, totalPages: 1, number: 1 }))
          }
        }
        return response
      } catch (error) {
        RootStore.response.setMessage(error?.message)
        self.pagination = JSON.parse(JSON.stringify({ size: 0, totalElements: 0, totalPages: 1, number: 1 }))
        throw error
      }
    }),
    fetchBulkBeneficiaryList: flow(function* fetchBulkBeneficiaryList(mid: string) {
      try {
        const response = yield bulkBeneficiaryFileHistory(
          RootStore.user.session.getTokens,
          'BULK_BENEFICIARY',
          self.pagination.number,
          10,
          self.bulkSelectedDateInfo,
          mid,
        )
        self.bulkBeneficiaryList = response?.response?.data?.content
        if (response?.response?.data?.content?.length > 0) {
          self.pagination = JSON.parse(JSON.stringify(response?.page))
        } else {
          self.pagination = JSON.parse(JSON.stringify({ size: 0, totalElements: 0, totalPages: 1, number: 1 }))
        }
        return response
      } catch (error) {
        RootStore.response.setMessage(error?.message)
        self.pagination = JSON.parse(JSON.stringify({ size: 0, totalElements: 0, totalPages: 1, number: 1 }))
        throw error
      }
    }),
    exportBeneficiaryReportsApi: flow(function* exportBeneficiaryReportsApi(mid: string, fileType: string) {
      const req = {
        processDefinitionKeyType: 'BENEFICIARY_LIST',
        identificationNumber: mid,
        type: '101',
        fileType: exportType.get(fileType),
      }
      let resp
      try {
        resp = yield exportBeneficiariesListFile(RootStore.user.session.getTokens, req)
        return resp
      } catch (e) {
        RootStore?.message?.setMessage(e?.message ?? 'Error while processing')
      }
      return resp
    }),
    exportBulkBeneficiaryReport: flow(function* exportBeneficiaryReportsApi() {}),
    downloadSampleFile: flow(function* downloadSampleFile(fileName: string) {
      try {
        // const resp = yield sampleFileDownload(filename, RootStore.user.session.getTokens)
        // let errorObj
        // try {
        //   if (resp?.data?.byteLength <= 100) {
        //     errorObj = JSON.parse(new TextDecoder('utf-8').decode(resp?.data))
        //   }
        // } catch (e) {}
        // if (errorObj) {
        //   throw errorObj
        // }
        // let contentType = resp.headers['content-type']
        // //check for undefined file download on session expire
        // if (mimeTypes.get(contentType) === undefined) {
        //   return
        // }
        // let fileInfo = resp.headers['content-disposition']
        // const fileName = fileInfo?.slice(fileInfo.indexOf('=') + 1) ?? 'file'
        // const blob = new Blob([resp?.data], {
        //   type: contentType,
        // })
        // const url = window.URL.createObjectURL(blob)
        // const a = document.createElement('a')
        // document.body.appendChild(a)
        // a.href = url
        // a.download = fileName + `${mimeTypes.get(contentType)}`
        // a.click()
        // window.URL.revokeObjectURL(url)
        // document.body.removeChild(a)
        console.log('window?._env_?.REACT_APP_URL', window?._env_?.REACT_APP_URL)
        const appUrl = window?._env_?.REACT_APP_URL ? window?._env_?.REACT_APP_URL : 'http://10.157.255.94:9966'
        const url = `${appUrl}/${fileName}`
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = fileName + '.xlsx'
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        RootStore?.response?.setMessage(getErrorMessage(error))
      }
    }),
    addSingleBeneficiary: flow(function* addSingleBeneficiary(mid: string, beneficiaryData: any) {
      let response
      try {
        response = yield addBeneficiary(
          mid,
          beneficiaryData,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const responseObj = response
        if (response && response?.error && response?.error?.message?.length > 0) {
          RootStore?.response?.setMessage(response?.error?.message ?? customError.errorOccured)
        }
        console.log(responseObj)
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    uploadBulkBeneficiaryFile: flow(function* uploadBulkBeneficiaryFile(
      mid: string,
      fileName: string,
      fileSize: string,
      file: any,
    ) {
      let res
      try {
        res = yield uploadFileReq(mid, fileName, fileSize, RootStore.user.session.getTokens, file)
        const errorCode = res?.error?.code ?? ''
        console.log({ errorCode })
        if (errorCode === '33607' || errorCode === '33306') {
          if (errorCode === '33607') {
            extendUserSessionAPI().then((response) => {
              console.log('extendUserSessionAPI response', response)
              return (self as any).uploadBulkBeneficiaryFile(mid, fileName, fileSize, file)
            })
          } else {
            extendAppSessionAPI().then((response) => {
              console.log('extendAppSessionAPI response', response)
              return (self as any).uploadBulkBeneficiaryFile(mid, fileName, fileSize, file)
            })
          }
        }
        if (res?.status === 'FAILED') {
          // TODO: show error based on code
          self.uploadStatus = 'NOT_SELECTED'
          RootStore?.response?.setMessage(
            res?.statusMessage ?? 'Invalid file selected, Please download sample file and upload in same format',
          )
        } else if (res?.status === 'INITIATED') {
          self.uploadedFileId = res?.id
          self.uploadStatus = 'UPLOADED'
          self.count = res?.count ?? null
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
      return res
    }),
    getBulkBeneficiaryUploadConfirm: flow(function* getBulkBeneficiaryUploadConfirm(fileId: string) {
      let res
      try {
        res = yield bulkRefundUploadConfirmReq(fileId)
        console.log(res)
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
      return res
    }),
    setUploadStatus: (
      status: 'NOT_SELECTED' | 'IN_PROGRESS' | 'UPLOADED' | 'IN_PROCESS' | 'PROCESSED' = 'NOT_SELECTED',
    ) => {
      self.uploadStatus = status
    },
    updateSelectedDate(dateObj: any): void {
      const own = self
      own.selectedDate.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.selectedDate.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.selectedDate.position.top = dateObj?.position?.top ?? 0
      own.selectedDate.position.left = dateObj?.position?.left ?? 0
      own.selectedDate.position.visible = dateObj?.position?.visible ?? false
      own.selectedDate.dateLabel = dateObj?.dateLabel
    },
    updateBulkSelectedDate(dateObj: any): void {
      const own = self
      own.bulkSelectedDateInfo.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.bulkSelectedDateInfo.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.bulkSelectedDateInfo.position.top = dateObj?.position?.top ?? 0
      own.bulkSelectedDateInfo.position.left = dateObj?.position?.left ?? 0
      own.bulkSelectedDateInfo.position.visible = dateObj?.position?.visible ?? false
      own.bulkSelectedDateInfo.dateLabel = dateObj?.dateLabel
    },
    resetPagination(): void {
      self.pagination = JSON.parse(JSON.stringify(paginationSettingsWithDefaultPage1))
    },
    setSize: (size: number | null) => {
      self.size = size
    },
    setCount: (count: number | null) => {
      self.count = count
    },
    getLovsFromKey(key: string) {
      const data = self.lovs.map((item, index) => {
        if (item.key === key) {
          return {
            id: index,
            value: item.value,
            description: item.description,
          }
        }
      })
      return data?.filter((item) => item !== undefined)
    },
    addNewBeneficiaryType: flow(function* addNewBeneficiaryType(entityId: string, key: string, value: string) {
      let response
      const configValues: any[] = [{ key: key, value: value }]
      console.log('===> addNewBeneficiaryType configValues,: ', configValues)
      if (configValues.length !== 0) {
        try {
          response = yield createConfigOfEntity(entityId, configValues, [], RootStore.user.session.getTokens)
          console.log('===> addNewBeneficiaryType response: ', response)
          if (response?.data?.error?.message?.length > 0) {
            RootStore.response.setMessage(response?.data?.error?.message)
          } else {
            RootStore.response.setMessage('Added Successfully')
          }
        } catch (error) {
          console.log('===> addNewBeneficiaryType error: ', error)
          RootStore.response.setMessage(getErrorMessage(error))
          throw error
        }
      }
      return response
    }),
    bulkOnboardfileUpload: flow(function* bulkOnboardfileUpload(file: any, mid: string) {
      try {
        self.fileUploaded = false
        const resp = yield bulkOnboardingFileUpload(file, mid, RootStore.user.session.getTokens)
        if (resp?.data?.status === 'UPLOADED') {
          self.uploadedFile = resp?.data
          self.uploadedFile.id = resp?.data?.id
          self.uploadStatus = 'UPLOADED'
        }
        let errorCode = ''
        try {
          errorCode = resp?.data?.error?.code ?? ''
          if (errorCode === '33607' || errorCode === '33306') {
            if (errorCode === '33607') {
              extendUserSessionAPI().then((response) => {
                console.log('extendUserSessionAPI response', response)
                return (self as any).bulkOnboardfileUpload(file, mid)
              })
            } else {
              extendAppSessionAPI().then((response) => {
                console.log('extendAppSessionAPI response', response)
                return (self as any).bulkOnboardfileUpload(file, mid)
              })
            }
          }
        } catch (err) {
          console.log(err)
        }
        if (errorCode === '') {
          self.fileUploaded = true
          const customToast = {
            color: 'GREEN',
            message: resp?.message ?? 'Bulk Beneficiary added successfully.',
            position: ToastPosition.TOP,
          }
          RootStore.response.setCustomToast(customToast)
        } else {
          if (errorCode !== '33607' && errorCode !== '33306') {
            RootStore.response.setMessage(resp?.data?.error?.message)
          }
        }
        return resp
      } catch (error) {
        RootStore.response.setMessage(getErrorMessage(error))
      }
    }),
    downloadBulkOnboardingFile: flow(function* downloadBulkOnboardingFile(fileId: number) {
      try {
        const response = yield bulkOnboardingFileDownload(fileId, RootStore.user.session.getTokens)
        return response
      } catch (error) {
        RootStore.response.setMessage(error?.data?.error?.message)
      }
    }),
  }))
  .views((self) => ({
    getBeneficiaryAllHistory() {
      let list = []
      self.beneficiariesList.map((bene) => {
        list.push({
          beneficiaryName: bene?.legalName,
          mobileNumber: bene?.mobileNumber,
          emailId: bene?.emailId,
          beneficiaryId: bene?.identificationNumber,
          subType: bene?.subType,
          accountNumber: bene?.bankAccountNumber,
        })
      })
      return list
    },
    getSplitBeneficiaries() {
      return self.beneficiariesList?.map((item) => {
        return {
          id: item?.identificationNumber,
          name: item?.legalName,
          mobile: item?.mobileNumber,
          emailId: item?.emailId,
          userType: isEmpty(item?.type) ? 'BENEFICIARY' : item?.type,
          splitAmount: 0,
          splitPercentage: 0,
        }
      })
    },
    getBeneficiaryBulkHistory() {
      let list = []
      self.bulkBeneficiaryList.map((bulk) => {
        list.push({
          id: bulk?.id,
          fileName: bulk?.filename,
          uploadDate: bulk?.updatedAt,
          totalCount: bulk?.totalBeneficiaries,
          addedCount: bulk?.acceptedBeneficiaries,
          declinedCount: bulk?.failedBeneficiaries,
          beneficiaryStatus: bulk?.status,
          actionDownload: bulk?.status?.toLowerCase() === 'completed' ? 'Download' : ' Download',
        })
      })
      return list
    },
  }))

export const BeneficiaryOnboardingData = BeneficiaryOnboarding.create({
  id: '',
  selectedDate: {
    fromDate: getSelectedDate(1),
    toDate: new Date(Date.now()),
    dateLabel: 'Today',
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
  },
  bulkSelectedDateInfo: {
    fromDate: getSelectedDate(1),
    toDate: new Date(Date.now()),
    dateLabel: 'Today',
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
  },
  pagination: paginationSettingsWithDefaultPage1,
  fieldSetting: [],
  beneficiariesList: [],
  bulkBeneficiaryList: [],
})

export type BeneficiaryOnboardingType = Instance<typeof BeneficiaryOnboarding>
export { BeneficiaryOnboarding }
